/* eslint-disable complexity */

import React, { useMemo } from 'react';
import {
  formatCurrency,
  useLocale,
  useTranslations,
} from '@unisporkal/localization';
import acquisitionTypes from '../../../support/acquisitionTypes';
import formatPrice from '../../../support/formatPrice';
import styles from './Price.module.scss';
import useSelectedUltraPack from '../../../shared/hooks/useSelectedUltraPack';
import { useAcquisitionType } from '../../../hooks/stateful/useAcquisitionType';
import useSelectedIndex from '../../../hooks/stateful/useSelectedIndex';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import UltraPackLicensePrice from './components/UltraPackLicensePrice/UltraPackLicensePrice';

const Price = () => {
  const { selectedSize } = useSelectedIndex();
  const {
    response: {
      config: {
        hasPreferredPricingVisible,
        isAgencyUnlimitedCreativeBuyer,
        isOpaAboveListPurchase,
      } = {},
    } = {},
  } = useOnLoad();

  const hasOpaAboveListPrice =
    selectedSize?.rawPrice > selectedSize?.rawOriginalPrice;

  const showOpaAboveListPricing = Boolean(
    isOpaAboveListPurchase && hasOpaAboveListPrice
  );

  const { acquisitionType } = useAcquisitionType();
  const { selectedUltraPack } = useSelectedUltraPack();
  const t = useTranslations();
  const locale = useLocale();
  const ultraPackPriceView =
    acquisitionType === acquisitionTypes.ULTRA_PACK_UPSELL;
  const discountPriceCheck = selectedSize?.discountPrice;

  const selectedUltraPackData = useMemo(
    () =>
      selectedSize?.imagePackSavings?.find(
        (imagePack) => imagePack.packSize === selectedUltraPack
      ),
    [selectedSize?.imagePackSavings, selectedUltraPack]
  );

  const formattedUltraPackPrice = useMemo(() => {
    const ultraPackPrice =
      ultraPackPriceView && selectedSize && selectedUltraPackData;
    const { formattedPrice } = ultraPackPrice
      ? formatCurrency(
          ultraPackPrice?.packPrice,
          locale,
          selectedSize.currencyCode,
          { minimumFractionDigits: 0, separateGeoTaxFromPrice: true }
        )
      : { formattedPrice: '' };
    return formattedPrice;
  }, [ultraPackPriceView, selectedSize, selectedUltraPackData, locale]);

  const preferredPriceView = () => {
    const {
      geoTaxDisclosure: selectedSizeTaxDisclosure,
      formattedPrice: selectedSizePrice,
    } = formatCurrency(
      selectedSize?.rawPrice,
      locale,
      selectedSize?.currencyCode,
      { separateGeoTaxFromPrice: true }
    );
    const priceInfo = {
      currencyCode: selectedSize?.currencyCode,
      price:
        formattedUltraPackPrice ||
        selectedSize?.discountPrice ||
        selectedSizePrice,
    };

    const discountedPriceInfo = {
      currencyCode: selectedSize?.currencyCode,
      price: selectedSizePrice,
    };

    return (
      <div
        className={styles.pricing}
        data-testid="price"
      >
        {!discountedPriceInfo.price && (
          <div data-testid="regular-price">
            <div className={styles.price}>{priceInfo.price}</div>
            <div className={styles.currency}>
              {selectedSizeTaxDisclosure} {priceInfo.currencyCode}
            </div>
          </div>
        )}
        {discountedPriceInfo.price && (
          <>
            <div
              className={styles.preferredPriceInfo}
              data-testid="preferred-price"
            >
              {t('your_preferred_price')}
            </div>
            <div
              className={styles.preferredPrice}
              data-testid="discount-price"
            >
              <div className={styles.price}>{discountedPriceInfo.price}</div>
              {selectedSizeTaxDisclosure ? (
                <span className={styles.finalPrice}>
                  {selectedSizeTaxDisclosure}
                </span>
              ) : null}
              <div className={styles.finalPrice}>
                {discountedPriceInfo.currencyCode}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const regularView = () => {
    const {
      geoTaxDisclosure: selectedSizeTaxDisclosure,
      formattedPrice: selectedSizePrice,
    } = formatCurrency(
      selectedSize?.rawPrice,
      locale,
      selectedSize?.currencyCode,
      { separateGeoTaxFromPrice: true }
    );

    const priceInfo = {
      currencyCode: selectedSize?.currencyCode,
      price:
        formattedUltraPackPrice ||
        selectedSize?.discountPrice ||
        selectedSizePrice,
    };

    return (
      <div
        className={styles.pricing}
        data-testid="price"
      >
        {showOpaAboveListPricing && (
          <div
            className={styles.preferredPriceInfo}
            data-testid="opa-above-list-price-label"
          >
            {t('your_enterprise_agreement_price')}
          </div>
        )}
        <div className={styles.price}>{priceInfo.price}</div>
        <div className={styles.currency}>
          {selectedSizeTaxDisclosure} {priceInfo.currencyCode}
        </div>
        {ultraPackPriceView && (
          <div
            className={styles.ultraPackNotes}
            data-testid="ultra-pack-compare"
          >
            {t('compared_with_single_price', {
              price: formatPrice(selectedSizePrice),
            })}
            {selectedSizeTaxDisclosure ? (
              <span className={styles.geoTax}>{selectedSizeTaxDisclosure}</span>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const suggestedRetailPriceView = () => {
    const {
      geoTaxDisclosure: selectedSizeTaxDisclosure,
      formattedPrice: selectedSizePrice,
    } = formatCurrency(
      selectedSize?.rawPrice,
      locale,
      selectedSize?.currencyCode,
      { separateGeoTaxFromPrice: true }
    );

    const priceInfo = {
      currencyCode: selectedSize?.currencyCode,
      price: selectedSizePrice,
    };

    return (
      <div
        className={styles.pricing}
        data-testid="price"
      >
        <div
          className={styles.preferredPriceInfo}
          data-testid="suggested-retail-price-label"
        >
          {t('discounted_price')}
        </div>
        <div
          className={styles.preferredPrice}
          data-testid="discount-price"
        >
          <div className={styles.price}>{priceInfo.price}</div>
          {selectedSizeTaxDisclosure ? (
            <span className={styles.finalPrice}>
              {selectedSizeTaxDisclosure}
            </span>
          ) : null}
          <div className={styles.finalPrice}>{priceInfo.currencyCode}</div>
        </div>
      </div>
    );
  };

  const hasSizeWithPrice =
    selectedSize &&
    Object.keys(selectedSize).length > 0 &&
    !selectedSize.license;

  const hasUltrapackLicense = selectedSize?.license === 'ultrapack';

  if (!hasUltrapackLicense && !hasSizeWithPrice) {
    return null;
  }

  const priceView = () => {
    if (hasUltrapackLicense) {
      return <UltraPackLicensePrice />;
    }
    if (isAgencyUnlimitedCreativeBuyer) {
      return suggestedRetailPriceView();
    }
    if (showOpaAboveListPricing) {
      return regularView();
    }
    return hasPreferredPricingVisible && discountPriceCheck
      ? preferredPriceView()
      : regularView();
  };

  return <>{priceView()}</>;
};

export default Price;
